import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { ButtonWithLoading } from '@actual-app/components/button';
import { BigInput } from '@actual-app/components/input';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';

import { Link } from '#components/common/Link';

import { Title } from './common';
import { getSaasErrorMessage } from './SaasLoginForm';

// With a ?token= this page sets a new password; without one it asks for the
// account email and sends the reset link.
export function ResetPassword() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onRequestReset() {
    if (email === '' || loading) return;
    setError(null);
    setLoading(true);
    const result = await send('saas-forgot-password', { email });
    setLoading(false);
    if ('error' in result) {
      setError(result.error);
    } else {
      setDone(true);
    }
  }

  async function onResetPassword() {
    if (password === '' || loading) return;
    if (password !== confirmPassword) {
      setError('password-mismatch');
      return;
    }
    setError(null);
    setLoading(true);
    const result = await send('saas-reset-password', {
      token: token!,
      newPassword: password,
    });
    setLoading(false);
    if ('error' in result) {
      setError(result.error);
    } else {
      setDone(true);
    }
  }

  function errorMessage(err: string): string {
    if (err === 'password-mismatch') {
      return t('Passwords do not match');
    }
    if (err === 'invalid-token' || err === 'token-expired') {
      return t('This reset link is invalid or has expired. Request a new one.');
    }
    return getSaasErrorMessage(t, err);
  }

  return (
    <View style={{ maxWidth: 450, marginTop: -30, color: theme.pageText }}>
      <Title text={t('Reset your password')} />

      {done ? (
        <Text style={{ fontSize: 16 }}>
          {token ? (
            <Trans>
              Your password has been reset. You can{' '}
              <Link variant="internal" to="/login">
                sign in
              </Link>{' '}
              with your new password.
            </Trans>
          ) : (
            <Trans>
              If an account exists for that email, a reset link is on its way.
              Check your inbox.
            </Trans>
          )}
        </Text>
      ) : token ? (
        <View style={{ flexDirection: 'column', gap: '0.75rem' }}>
          <BigInput
            autoFocus
            placeholder={t('New password (at least 8 characters)')}
            type="password"
            onChangeValue={setPassword}
            onEnter={onResetPassword}
          />
          <BigInput
            placeholder={t('Confirm new password')}
            type="password"
            onChangeValue={setConfirmPassword}
            onEnter={onResetPassword}
          />
          <ButtonWithLoading
            variant="primary"
            isLoading={loading}
            onPress={onResetPassword}
          >
            <Trans>Set new password</Trans>
          </ButtonWithLoading>
        </View>
      ) : (
        <View style={{ flexDirection: 'column', gap: '0.75rem' }}>
          <Text style={{ fontSize: 15, color: theme.pageTextDark }}>
            <Trans>
              Enter your account email and we will send you a link to reset your
              password.
            </Trans>
          </Text>
          <BigInput
            autoFocus
            placeholder={t('Email')}
            type="email"
            onChangeValue={setEmail}
            onEnter={onRequestReset}
          />
          <ButtonWithLoading
            variant="primary"
            isLoading={loading}
            onPress={onRequestReset}
          >
            <Trans>Send reset link</Trans>
          </ButtonWithLoading>
        </View>
      )}

      {!done && (
        <Text
          style={{ fontSize: 14, color: theme.pageTextLight, marginTop: 12 }}
        >
          <Trans>
            Remembered it?{' '}
            <Link variant="internal" to="/login">
              Sign in
            </Link>
          </Trans>
        </Text>
      )}

      {error && (
        <Text style={{ marginTop: 15, color: theme.errorText, fontSize: 15 }}>
          {errorMessage(error)}
        </Text>
      )}
    </View>
  );
}
